import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  Stack,
  FormLabel,
  TableSortLabel,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DashboardIcon from "@mui/icons-material/Dashboard";

import {
  COLORS,
  SCROLLBAR_STYLES,
  findKey,
  renderFilterValue,
} from "./utils/rtmUtils";
import AnalyticsKPICard from "./components/AnalyticsKPICard";
import AnalyticsFilterBar from "./components/AnalyticsFilterBar";
import AnalyticsChart from "./components/AnalyticsChart";

const UserStoryRTMDashboard = ({ data }) => {
  const [filters, setFilters] = useState({
    monthYear: ["All"],
    sprint: ["All"],
    version: ["All"],
    priority: ["All"],
    status: ["All"],
    type: ["All"],
  });

  const [sortConfig, setSortConfig] = useState({
    key: "key",
    direction: "asc",
  });
  const [tableFilter, setTableFilter] = useState("All");

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const sample = data[0];
    const actualKeys = {
      key: findKey(sample, "Issue Key") || "Issue key",
      type: findKey(sample, "Issue Type") || "Issue Type",
      summary: findKey(sample, "Summary") || "Summary",
      linkedId: findKey(sample, "Linked Issue Key") || "Linked Issues ID",
      linkedType: findKey(sample, "Linked Issue Type") || "Linked Issue Type",
      status: findKey(sample, "Status") || "Status",
      priority: findKey(sample, "Priority") || "Priority",
      created:
        findKey(sample, "Created Date") ||
        findKey(sample, "Created") ||
        "Created",
      project: findKey(sample, "Project") || "Project",
      labels: findKey(sample, "Labels") || "Labels",
      sprint: findKey(sample, "Sprint") || "Sprint",
      version:
        findKey(sample, "Release Version") ||
        findKey(sample, "Version") ||
        "Fix Version/s",
    };

    const aggregated = {};

    data.forEach((row) => {
      const issueKey = row[actualKeys.key];
      if (!issueKey) return;

      if (!aggregated[issueKey]) {
        aggregated[issueKey] = {
          key: issueKey,
          type: row[actualKeys.type] || "Unknown",
          summary: row[actualKeys.summary] || "",
          status: row[actualKeys.status] || "To Do",
          priority: row[actualKeys.priority] || "Medium",
          created: row[actualKeys.created],
          project: row[actualKeys.project] || "N/A",

          labels: row[actualKeys.labels] || "",
          sprint: row[actualKeys.sprint] || "No Sprint",
          version: row[actualKeys.version] || "No Version",
          testCount: 0,
          bugCount: 0,
          otherCount: 0,
          linkedItems: new Set(),
          testItems: new Set(),
          bugItems: new Set(),
        };
      }

      const lIdRaw = row[actualKeys.linkedId];
      const lTypeRaw = row[actualKeys.linkedType];

      if (lIdRaw) {
        const ids = String(lIdRaw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const types = lTypeRaw
          ? String(lTypeRaw)
              .split(",")
              .map((s) => s.trim())
          : [];

        ids.forEach((id, index) => {
          const type = (types[index] || types[0] || "").toLowerCase();

          if (!aggregated[issueKey].linkedItems.has(id)) {
            aggregated[issueKey].linkedItems.add(id);

            const isTest =
              type.includes("test") || id.toUpperCase().startsWith("TEST");
            const isBug =
              type.includes("bug") ||
              type.includes("defect") ||
              id.toUpperCase().startsWith("BUG");

            if (isTest) {
              aggregated[issueKey].testItems.add(id);
            } else if (isBug) {
              aggregated[issueKey].bugItems.add(id);
            } else {
              aggregated[issueKey].otherCount++;
            }
          }
        });
      }
    });

    return Object.values(aggregated).map((issue) => {
      const testCount = issue.testItems.size;
      const bugCount = issue.bugItems.size;
      return {
        ...issue,
        testCount,
        bugCount,
        hasTest: testCount > 0,
        hasBug: bugCount > 0,
        coverage: testCount > 0 ? "Covered" : "Not Covered",
        createdDate: issue.created ? new Date(issue.created) : null,
        monthYear: issue.created
          ? new Date(issue.created).toLocaleString("default", {
              month: "short",
              year: "numeric",
            })
          : "Unknown",
      };
    });
  }, [data]);

  const filterOptions = useMemo(
    () => ({
      sprint: [
        "All",
        ...new Set(processedData.map((d) => String(d.sprint))),
      ].filter((s) => s !== "undefined"),
      version: [
        "All",
        ...new Set(processedData.map((d) => String(d.version))),
      ].filter((v) => v !== "undefined"),
      priority: [
        "All",
        ...new Set(processedData.map((d) => String(d.priority))),
      ].filter((p) => p !== "undefined"),
      status: [
        "All",
        ...new Set(processedData.map((d) => String(d.status))),
      ].filter((s) => s !== "undefined"),
      type: [
        "All",
        ...new Set(processedData.map((d) => String(d.type))),
      ].filter((t) => t !== "undefined"),
      monthYear: [
        "All",
        ...new Set(processedData.map((d) => String(d.monthYear))),
      ].filter((m) => m !== "Unknown"),
    }),
    [processedData],
  );

  const filteredData = useMemo(
    () =>
      processedData.filter(
        (d) =>
          (filters.monthYear.includes("All") ||
            filters.monthYear.includes(String(d.monthYear))) &&
          (filters.sprint.includes("All") ||
            filters.sprint.includes(String(d.sprint))) &&
          (filters.version.includes("All") ||
            filters.version.includes(String(d.version))) &&
          (filters.priority.includes("All") ||
            filters.priority.includes(String(d.priority))) &&
          (filters.status.includes("All") ||
            filters.status.includes(String(d.status))) &&
          (filters.type.includes("All") ||
            filters.type.includes(String(d.type))),
      ),
    [processedData, filters],
  );

  const stats = useMemo(() => {
    const total = filteredData.length;
    const uniqueLinks = new Set();
    filteredData.forEach((d) =>
      d.linkedItems.forEach((id) => uniqueLinks.add(id)),
    );

    const coveredCount = filteredData.filter((d) => d.hasTest).length;
    const withoutTests = total - coveredCount;
    const withBugs = filteredData.filter((d) => d.hasBug).length;

    const done = filteredData.filter((d) =>
      ["DONE", "RESOLVED", "CLOSED", "COMPLETED"].includes(
        String(d.status).toUpperCase(),
      ),
    ).length;
    const inProgress = filteredData.filter((d) =>
      ["IN PROGRESS", "DEVELOPMENT", "TESTING", "IN REVIEW"].includes(
        String(d.status).toUpperCase(),
      ),
    ).length;
    const toDo = total - done - inProgress;

    return {
      total,
      linkedTotal: uniqueLinks.size,
      coveragePercent: total > 0 ? Math.round((coveredCount / total) * 100) : 0,
      withoutTests,
      withBugs,
      donePercent: total > 0 ? Math.round((done / total) * 100) : 0,
      inProgress,
      toDo,
    };
  }, [filteredData]);

  const insights = useMemo(() => {
    const list = [];
    const criticalHighNotCovered = filteredData.filter((d) => {
      const p = String(d.priority).toUpperCase();
      const isHighPrio =
        p.includes("HIGH") ||
        p.includes("CRITICAL") ||
        p.includes("BLOCKER") ||
        p.includes("P0") ||
        p.includes("P1");
      return isHighPrio && !d.hasTest;
    });

    if (criticalHighNotCovered.length > 0)
      list.push({
        text: `${criticalHighNotCovered.length} Critical/High priority requirements have NO test coverage`,
        type: "error",
      });
    if (stats.withoutTests > 0)
      list.push({
        text: `${stats.withoutTests} total requirements have NO test coverage`,
        type: "error",
      });

    const highPrioToDo = filteredData.filter((d) => {
      const p = String(d.priority).toUpperCase();
      const isHighPrio =
        p.includes("HIGH") ||
        p.includes("CRITICAL") ||
        p.includes("BLOCKER") ||
        p.includes("P0") ||
        p.includes("P1");
      return (
        isHighPrio &&
        !["DONE", "RESOLVED", "CLOSED"].includes(d.status.toUpperCase())
      );
    });

    if (highPrioToDo.length > 0)
      list.push({
        text: `${highPrioToDo.length} high-priority issues are still not completed`,
        type: "warning",
      });

    const multipleBugs = [...filteredData]
      .filter((d) => d.bugCount > 1)
      .sort((a, b) => b.bugCount - a.bugCount);
    if (multipleBugs.length > 0)
      list.push({
        text: `Requirement ${multipleBugs[0].key} has multiple (${multipleBugs[0].bugCount}) bugs`,
        type: "error",
      });

    if (stats.coveragePercent < 80)
      list.push({
        text: `Overall coverage is below target (Current: ${stats.coveragePercent}%)`,
        type: "warning",
      });
    else
      list.push({
        text: `Test coverage is healthy at ${stats.coveragePercent}%`,
        type: "success",
      });

    return list;
  }, [filteredData, stats]);

  const tableData = useMemo(() => {
    let data = [...filteredData];
    if (tableFilter === "Covered") data = data.filter((d) => d.hasTest);
    else if (tableFilter === "Not Covered")
      data = data.filter((d) => !d.hasTest);

    data.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (sortConfig.key === "createdDate") {
        aVal = a.createdDate ? a.createdDate.getTime() : 0;
        bVal = b.createdDate ? b.createdDate.getTime() : 0;
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [filteredData, tableFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // --- Chart Data Memos ---

  const monthYearLabels = useMemo(
    () =>
      [
        ...new Set(
          filteredData
            .filter((d) => d.monthYear !== "Unknown")
            .map((d) => d.monthYear),
        ),
      ].sort((a, b) => new Date(a) - new Date(b)),
    [filteredData],
  );

  const coverageTrendData = useMemo(
    () => ({
      labels: monthYearLabels,
      datasets: [
        {
          label: "Covered",
          data: monthYearLabels.map(
            (m) =>
              filteredData.filter((d) => d.monthYear === m && d.hasTest)
                .length,
          ),
          borderColor: COLORS.DONE,
          fill: false,
        },
        {
          label: "Not Covered",
          data: monthYearLabels.map(
            (m) =>
              filteredData.filter((d) => d.monthYear === m && !d.hasTest)
                .length,
          ),
          borderColor: COLORS.FAIL,
          fill: false,
        },
        {
          label: "Total Created",
          data: monthYearLabels.map(
            (m) => filteredData.filter((d) => d.monthYear === m).length,
          ),
          borderColor: COLORS.SECONDARY,
          borderDash: [5, 5],
          fill: false,
        },
      ],
    }),
    [monthYearLabels, filteredData],
  );

  const creationTrendData = useMemo(
    () => ({
      labels: monthYearLabels,
      datasets: [
        {
          label: "Issues Created",
          data: monthYearLabels.map(
            (m) => filteredData.filter((d) => d.monthYear === m).length,
          ),
          borderColor: COLORS.OPEN,
          fill: false,
        },
      ],
    }),
    [monthYearLabels, filteredData],
  );

  const statusData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [
        { data: Object.values(counts), backgroundColor: COLORS.PALETTE },
      ],
    };
  }, [filteredData]);

  const priorityData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => {
      acc[d.priority] = (acc[d.priority] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [
        {
          label: "Issues",
          data: Object.values(counts),
          backgroundColor: COLORS.SECONDARY,
        },
      ],
    };
  }, [filteredData]);

  const typeData = useMemo(() => {
    const counts = filteredData.reduce((acc, d) => {
      acc[d.type] = (acc[d.type] || 0) + 1;
      return acc;
    }, {});
    return {
      labels: Object.keys(counts),
      datasets: [
        { data: Object.values(counts), backgroundColor: COLORS.PALETTE },
      ],
    };
  }, [filteredData]);

  const coverageSummaryData = useMemo(
    () => ({
      labels: ["Covered", "Not Covered"],
      datasets: [
        {
          data: [stats.total - stats.withoutTests, stats.withoutTests],
          backgroundColor: [COLORS.DONE, COLORS.FAIL],
        },
      ],
    }),
    [stats],
  );

  const rtmStackedData = useMemo(
    () => ({
      labels: filteredData.map((d) => d.key),
      datasets: [
        {
          label: "Tests",
          data: filteredData.map((d) => d.testCount),
          backgroundColor: COLORS.OPEN,
        },
        {
          label: "Bugs",
          data: filteredData.map((d) => d.bugCount),
          backgroundColor: COLORS.FAIL,
        },
      ],
    }),
    [filteredData],
  );

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          bgcolor: "white",
          boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)",
        }}
      >
        <AnalyticsFilterBar
          filters={filters}
          options={filterOptions}
          onFilterChange={handleFilterChange}
        />
      </Paper>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={2}>
          <AnalyticsKPICard
            title="Total"
            value={stats.total}
            icon={<DashboardIcon />}
            color={COLORS.PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <AnalyticsKPICard
            title="Coverage"
            value={`${stats.coveragePercent}%`}
            icon={<TrendingUpIcon />}
            color={COLORS.DONE}
            subtitle={`${stats.total - stats.withoutTests} covered`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <AnalyticsKPICard
            title="Not Covered"
            value={stats.withoutTests}
            icon={<ErrorOutlineIcon />}
            color={COLORS.FAIL}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <AnalyticsKPICard
            title="With Bugs"
            value={stats.withBugs}
            icon={<BugReportIcon />}
            color={COLORS.TO_DO}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <AnalyticsKPICard
            title="Done"
            value={`${stats.donePercent}%`}
            icon={<CheckCircleOutlineIcon />}
            color={COLORS.DONE}
            subtitle={`In Progress: ${stats.inProgress}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <AnalyticsKPICard
            title="Linked Items"
            value={stats.linkedTotal}
            icon={<AssignmentIcon />}
            color={COLORS.OPEN}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              Requirement Coverage Distribution
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 300, minWidth: 800 }}>
                <AnalyticsChart
                  type="line"
                  data={coverageTrendData}
                  options={{
                    scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
                  }}
                  height={300}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              Requirement Creation Distribution
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box sx={{ height: 320, minWidth: 600 }}>
                <AnalyticsChart
                  type="line"
                  data={creationTrendData}
                  options={{
                    scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
                  }}
                  height={300}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{ p: 2, height: "100%", borderRadius: 2, bgcolor: "#f5f7fa" }}
          >
            <Typography
              variant="h6"
              display="flex"
              alignItems="center"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY }}
            >
              <LightbulbIcon sx={{ mr: 1, color: "#ffc107" }} /> Smart Insights
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              {insights.map((insight, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: "4px solid",
                    borderLeftColor:
                      insight.type === "error"
                        ? COLORS.FAIL
                        : insight.type === "warning"
                          ? COLORS.TO_DO
                          : COLORS.DONE,
                    bgcolor: "white",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {insight.text}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              Test Coverage Summary
            </Typography>
            <Box sx={{ height: 310 }}>
              <AnalyticsChart
                type="pie"
                data={coverageSummaryData}
                options={{ legend: { position: "right" } }}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              RTM Coverage (Tests vs Bugs)
            </Typography>
            <Box sx={{ overflowX: "auto", width: "100%", ...SCROLLBAR_STYLES }}>
              <Box
                sx={{
                  height: 300,
                  minWidth: filteredData.length > 15 ? 1000 : "100%",
                }}
              >
                <AnalyticsChart
                  type="bar"
                  data={rtmStackedData}
                  options={{
                    scales: {
                      xAxes: [{ stacked: true }],
                      yAxes: [{ stacked: true, ticks: { beginAtZero: true } }],
                    },
                  }}
                  height={300}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              Type Distribution
            </Typography>
            <AnalyticsChart
              type="pie"
              data={typeData}
              options={{ legend: { position: "right" } }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              Status Distribution
            </Typography>
            <AnalyticsChart
              type="doughnut"
              data={statusData}
              options={{ cutoutPercentage: 70, legend: { position: "right" } }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: COLORS.PRIMARY, mb: 2 }}
            >
              Priority Distribution
            </Typography>
            <AnalyticsChart
              type="bar"
              data={priorityData}
              options={{
                scales: { yAxes: [{ ticks: { beginAtZero: true } }] },
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: COLORS.PRIMARY }}
              >
                Traceability Matrix
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                >
                  <MenuItem value="All">All Requirements</MenuItem>
                  <MenuItem value="Covered">Covered Only</MenuItem>
                  <MenuItem value="Not Covered">Not Covered Only</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TableContainer
              sx={{ maxHeight: 600, overflowY: "auto", ...SCROLLBAR_STYLES }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "key",
                      "type",
                      "summary",
                      "status",
                      "priority",
                      "createdDate",
                      "testCount",
                      "bugCount",
                      "coverage",
                    ].map((key) => (
                      <TableCell
                        key={key}
                        sx={{
                          fontWeight: 700,
                          bgcolor: "#f8f9fa",
                          color: COLORS.PRIMARY,
                        }}
                      >
                        {["summary", "coverage"].includes(key) ? (
                          key.toUpperCase()
                        ) : (
                          <TableSortLabel
                            active={sortConfig.key === key}
                            direction={
                              sortConfig.key === key
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSort(key)}
                          >
                            {key.toUpperCase()}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row) => (
                    <TableRow key={row.key} hover>
                      <TableCell>
                        <Chip
                          label={row.key}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, color: COLORS.PRIMARY }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.type}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip title={row.summary}>
                          <Typography variant="body2">{row.summary}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            bgcolor: ["DONE", "RESOLVED", "CLOSED"].includes(
                              row.status.toUpperCase(),
                            )
                              ? "#e8f5e9"
                              : "#fff3e0",
                            color: ["DONE", "RESOLVED", "CLOSED"].includes(
                              row.status.toUpperCase(),
                            )
                              ? COLORS.DONE
                              : COLORS.TO_DO,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.priority}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.monthYear}</Typography>
                      </TableCell>
                      <TableCell align="center">{row.testCount}</TableCell>
                      <TableCell align="center">{row.bugCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.coverage}
                          size="small"
                          color={row.hasTest ? "success" : "error"}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserStoryRTMDashboard;
